import { isDefined, isNil } from "@clipboard-health/util-ts";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import {
  InterviewStatus,
  transformResponseIntoInterview,
  useFetchPaginatedInterviews,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { type PlacementDetail } from "./useGetPlacement";

interface UseGetInterviewFromConnectedListingParams {
  placement: PlacementDetail;
}

export function useGetInterviewFromConnectedListing({
  placement,
}: UseGetInterviewFromConnectedListingParams) {
  const worker = useDefinedWorker();
  const { enabled, workplaceTypes } = useCbhFlag(CbhFeatureFlag.ENABLE_CONNECTED_JOB_LISTINGS, {
    defaultValue: { enabled: false },
  });

  const isConnectedJobListingsEnabled =
    enabled &&
    isDefined(workplaceTypes) &&
    (workplaceTypes.includes("*") ||
      (isDefined(placement.workplace.type) && workplaceTypes.includes(placement.workplace.type)));

  const query = useFetchPaginatedInterviews(
    {
      filter: {
        workerId: worker.userId,
        status: InterviewStatus.BOOKED,
        start: { gt: new Date().toISOString() },
        connectedToPlacementId: placement.id,
      },
      sort: "start",
      pageSize: 1,
    },
    {
      enabled: isConnectedJobListingsEnabled && isDefined(worker.userId) && isDefined(placement.id),
      queryKey: ["useGetInterviewFromConnectedListing", placement.id, worker.userId],
    }
  );

  const page = query.data?.pages?.[0];
  const interview = query.data?.pages?.[0]?.data?.[0];

  if (isNil(page) || isNil(interview)) {
    return {
      ...query,
      data: undefined,
    };
  }

  const interviewWithDetails = transformResponseIntoInterview(
    interview,
    page.included ?? [],
    worker.tmz ?? ""
  );

  return {
    ...query,
    data: interviewWithDetails,
  };
}
